/**
 * House of Morka favourites (☆) and email unlock, shared by every page.
 * Saved designs: localStorage `hom_favs` = [{ id, name, img, url, line }].
 * Unlock: the email goes to Shopify's customer (newsletter) form, tagged `favs-unlock`
 * plus `fav:<id>` per saved design; `hom_email` remembers it in this browser.
 * Events: `hom:favs-change` (detail.favs), `hom:unlocked` (detail.email).
 */
const KEY = 'hom_favs';
const EMAIL_KEY = 'hom_email';

const store = {
  get(key, fallback) {
    try {
      const v = localStorage.getItem(key);
      return v == null ? fallback : JSON.parse(v);
    } catch {
      return fallback;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Private mode: the choice still works on this page.
    }
  },
};

/** @param {string} s */
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

export const Favs = {
  /** @returns {{id:string,name:string,img:string,url:string,line:string}[]} */
  list() {
    const v = store.get(KEY, []);
    return Array.isArray(v) ? v.filter((x) => x && typeof x.id === 'string') : [];
  },
  /** @param {string} id */
  has(id) {
    return this.list().some((f) => f.id === id);
  },
  /** @param {{id:string,name:string,img:string,url:string,line:string}} item */
  toggle(item) {
    const list = this.list();
    const next = list.some((f) => f.id === item.id) ? list.filter((f) => f.id !== item.id) : [...list, item];
    store.set(KEY, next);
    document.dispatchEvent(new CustomEvent('hom:favs-change', { detail: { favs: next } }));
  },
  /** @param {string} id */
  remove(id) {
    const next = this.list().filter((f) => f.id !== id);
    store.set(KEY, next);
    document.dispatchEvent(new CustomEvent('hom:favs-change', { detail: { favs: next } }));
  },
  email() {
    const e = store.get(EMAIL_KEY, '');
    return typeof e === 'string' ? e : '';
  },
};
window.HomFavs = Favs;

class HomFavsElement extends HTMLElement {
  #controller = new AbortController();
  /** @type {{ ids: string[], after?: () => void } | null} */
  #gate = null;

  connectedCallback() {
    const { signal } = this.#controller;
    document.addEventListener('click', this.#onClick, { signal });
    document.addEventListener('keydown', this.#onKey, { signal });
    document.addEventListener('hom:favs-change', this.#paint, { signal });
    document.addEventListener('hom:unlocked', this.#paint, { signal });
    document.addEventListener('hom:open-gate', this.#onOpenGate, { signal });
    this.querySelector('[data-favs-form]')?.addEventListener('submit', this.#onSubmit, { signal });
    this.#paint();
    // Cards rendered later (sections loaded by the editor) get their state on the next change.
    document.addEventListener('shopify:section:load', this.#paint, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #paint = () => {
    const favs = Favs.list();
    const ids = new Set(favs.map((f) => f.id));
    for (const b of document.querySelectorAll('[data-fav]')) {
      if (b instanceof HTMLElement) b.setAttribute('aria-pressed', String(ids.has(b.dataset.fav ?? '')));
    }
    for (const c of document.querySelectorAll('[data-favs-count]')) c.textContent = favs.length ? String(favs.length) : '';

    const list = this.querySelector('[data-favs-list]');
    if (list) {
      list.innerHTML = favs.length
        ? favs
            .map(
              (f) =>
                // whole row opens the design (ARVI 2026-10-21: only the name used to be the link); ✕ stays outside the link
                `<div class="hom-favs__item"><a class="hom-favs__link" href="${esc(f.url)}"><img src="${esc(f.img)}" alt="" width="68" height="68" loading="lazy"><span><b>${esc(f.name)}</b><small>${esc(f.line)}</small></span></a><button type="button" class="hom-favs__rm" data-favs-remove="${esc(f.id)}" aria-label="Remove ${esc(f.name)}">✕</button></div>`
            )
            .join('')
        : '<div class="hom-favs__empty">Nothing saved yet.<br>Tap ☆ on any design.</div>';
    }
    const unlock = this.querySelector('[data-favs-unlock]');
    if (unlock instanceof HTMLElement) {
      unlock.hidden = !favs.length;
      const email = Favs.email();
      unlock.innerHTML = email
        ? `<span class="hom-favs__ok">✓ Unlocked for ${esc(email)}</span><span class="hom-favs__lead">Open any saved style and see the sketch in about two minutes. Add more any time.</span><a class="hom-favs__btn" href="${esc(favs[0]?.url ?? '/collections/all-styles')}">Upload their photo</a>`
        : `<b>Unlock these with your pet</b><span class="hom-favs__lead">One email. We draw your pet in every saved style as a free preview. Nothing printed until you approve.</span><button type="button" class="hom-favs__btn" data-favs-unlock-all>Unlock with email</button>`;
    }
  };

  /** @param {MouseEvent} e */
  #onClick = (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const star = t.closest('[data-fav]');
    if (star instanceof HTMLElement) {
      e.preventDefault();
      e.stopPropagation();
      Favs.toggle({
        id: star.dataset.fav ?? '',
        name: star.dataset.favName ?? '',
        img: star.dataset.favImg ?? '',
        url: star.dataset.favUrl ?? '',
        line: star.dataset.favLine ?? '',
      });
      return;
    }
    const rm = t.closest('[data-favs-remove]');
    if (rm instanceof HTMLElement) {
      Favs.remove(rm.dataset.favsRemove ?? '');
      return;
    }
    if (t.closest('[data-favs-open]')) {
      e.preventDefault();
      this.#show('tray');
      return;
    }
    if (t.closest('[data-favs-unlock-all]')) {
      this.#openGate({ ids: Favs.list().map((f) => f.id) });
      return;
    }
    if (t.closest('[data-favs-close]') || t.matches('[data-favs-tray], [data-favs-modal]')) {
      this.#hide();
    }
  };

  /** @param {KeyboardEvent} e */
  #onKey = (e) => {
    if (e.key === 'Escape') this.#hide();
  };

  /** @param {Event} e */
  #onOpenGate = (e) => {
    const detail = /** @type {CustomEvent} */ (e).detail ?? {};
    this.#openGate(detail);
  };

  /** @type {Promise<void> | null} */
  #sketchesLoad = null;

  /** "Your sketches": the rounds this visitor picked, from the studio by the visitor ID (ARVI 2026-10-19). Fetched once per page. */
  #sketches() {
    const api = this.dataset.studioApi;
    const box = this.querySelector('[data-favs-sketches]');
    const list = this.querySelector('[data-favs-sketch-list]');
    let vid = '';
    try {
      vid = window.homVid || localStorage.getItem('hom_vid') || localStorage.getItem('hom-sketch-vid') || '';
    } catch {
      vid = '';
    }
    if (!api || !(box instanceof HTMLElement) || !list || !vid || this.#sketchesLoad) return;
    this.#sketchesLoad = fetch(`${api}/mine?visitor=${encodeURIComponent(vid)}`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((j) => {
        const items = Array.isArray(j.items) ? j.items : [];
        box.hidden = !items.length;
        list.innerHTML = items
          .map((s) => {
            const img = `${api}/file/${esc(s.id)}/${esc(s.sketch)}?t=${esc(s.t)}`;
            const url = s.handle ? `/products/${esc(s.handle)}?sk=${esc(s.id)}.${esc(s.t)}.${esc(s.n)}` : '';
            const when = s.created ? new Date(s.created * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
            const name = `${esc(s.name)} · ${esc(s.title)}`;
            const body = `<img src="${img}" alt="" width="68" height="68" loading="lazy"><span><b>${name}</b><small>Sketch ${esc(s.n)}${when ? ` · ${when}` : ''} · tap to put it on a tee</small></span>`;
            // whole row is the link (ARVI 2026-10-21)
            return `<div class="hom-favs__item">${url ? `<a class="hom-favs__link" href="${url}">${body}</a>` : `<span class="hom-favs__link">${body}</span>`}</div>`;
          })
          .join('');
      })
      .catch(() => {
        box.hidden = true;
      });
  }

  /** @param {'tray'|'modal'} which */
  #show(which) {
    if (which === 'tray') this.#sketches();
    const tray = this.querySelector('[data-favs-tray]');
    const modal = this.querySelector('[data-favs-modal]');
    if (tray instanceof HTMLElement) tray.hidden = which !== 'tray';
    if (modal instanceof HTMLElement) modal.hidden = which !== 'modal';
    document.documentElement.style.overflow = 'hidden';
    const focus = which === 'modal' ? this.querySelector('input[type=email]') : this.querySelector('[data-favs-tray] [data-favs-close]');
    if (focus instanceof HTMLElement) setTimeout(() => focus.focus(), 30);
  }

  #hide() {
    for (const el of this.querySelectorAll('[data-favs-tray], [data-favs-modal]')) if (el instanceof HTMLElement) el.hidden = true;
    document.documentElement.style.overflow = '';
  }

  /** @param {{ ids?: string[], title?: string, text?: string, after?: () => void }} ctx */
  #openGate(ctx) {
    this.#gate = { ids: ctx.ids ?? [], after: ctx.after };
    const title = this.querySelector('[data-gate-title]');
    const text = this.querySelector('[data-gate-text]');
    const picked = this.querySelector('[data-gate-picked]');
    const n = this.#gate.ids.length;
    if (title) title.textContent = ctx.title ?? (n > 1 ? 'Unlock your favourites' : 'Unlock the free preview');
    if (text)
      text.textContent =
        ctx.text ??
        (n > 1
          ? `We draw your pet in any of your ${n} saved styles in about two minutes, right on the page. Free, no card needed.`
          : 'We draw your pet in this style in about two minutes, right on the page. Free, no card needed.');
    if (picked) {
      const imgs = this.#gate.ids
        .map((id) => document.querySelector(`[data-fav="${CSS.escape(id)}"]`))
        .map((b) => (b instanceof HTMLElement ? b.dataset.favImg : Favs.list().find((f) => this.#gate?.ids.includes(f.id))?.img))
        .filter(Boolean)
        .slice(0, 6);
      picked.innerHTML = imgs.map((src) => `<img src="${esc(src)}" alt="" width="52" height="52">`).join('');
    }
    const input = this.querySelector('input[type=email]');
    if (input instanceof HTMLInputElement) input.value = Favs.email();
    this.#show('modal');
  }

  /** @param {SubmitEvent} e */
  #onSubmit = async (e) => {
    e.preventDefault();
    const form = /** @type {HTMLFormElement} */ (e.currentTarget);
    const input = form.querySelector('input[type=email]');
    const err = form.querySelector('[data-gate-err]');
    const email = input instanceof HTMLInputElement ? input.value.trim() : '';
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (err instanceof HTMLElement) err.hidden = valid;
    if (!valid) return;

    const ids = this.#gate?.ids ?? [];
    const marketing = form.querySelector('input[name=marketing]');
    const tags = ['favs-unlock', ...ids.slice(0, 10).map((id) => `fav:${id}`)];
    if (marketing instanceof HTMLInputElement && marketing.checked) tags.push('newsletter');
    const body = new FormData();
    body.set('form_type', 'customer');
    body.set('utf8', '✓');
    body.set('contact[email]', email);
    body.set('contact[tags]', tags.join(','));
    if (marketing instanceof HTMLInputElement && marketing.checked) body.set('contact[accepts_marketing]', 'true');
    try {
      await fetch('/contact#contact_form', { method: 'POST', body, credentials: 'same-origin' });
    } catch {
      // Network trouble: keep the unlock in this browser; the concierge sees nothing yet.
    }
    store.set(EMAIL_KEY, email);
    const after = this.#gate?.after;
    this.#hide();
    document.dispatchEvent(new CustomEvent('hom:unlocked', { detail: { email, ids } }));
    if (after) after();
  };
}

if (!customElements.get('hom-favs')) customElements.define('hom-favs', HomFavsElement);
